import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { distinctUntilChanged, pairwise } from 'rxjs/operators';

import { AppLoggerService } from '@lib-shared/logger/services/app-logger.service';

import { injectAuthFeature } from '@mymill/core/auth';

/**
 * Journalise le cycle de vie de l'authentification vers Bugsink :
 * - résultat de chaque tentative de login (manuelle, auto-login ou refresh) avec le détail
 *   de l'erreur en cas d'échec — jamais les credentials ;
 * - tag `user_id` attaché à tous les événements suivants via AppLoggerService.setUser.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthEventsLoggerService {
  private readonly appLogger = inject(AppLoggerService);
  private readonly authFeature = injectAuthFeature();

  private initialized = false;

  /**
   * init
   *
   */
  init(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    // suit la session pour alimenter le tag user_id (login, restauration, logout)
    this.authFeature.user$
      .pipe(distinctUntilChanged((previous, current) => previous?.guid === current?.guid))
      .subscribe((user) => {
        this.appLogger.setUser(user ? { userId: user.guid } : null);
      });

    // fin d'une tentative de login : transition isLoggingInProgress true -> false
    this.authFeature.state$.pipe(pairwise()).subscribe(([previous, current]) => {
      if (!previous.isLoggingInProgress || current.isLoggingInProgress) {
        return;
      }

      if (!current.error && current.isLoggedIn) {
        this.appLogger.info('login_succeeded', {
          tags: { feature: 'auth' },
        });

        return;
      }

      this.appLogger.warning('login_failed', {
        tags: { feature: 'auth' },
        extra: { error: this.describeError(current.error) },
      });
    });
  }

  /**
   * describeError
   *
   * Description sûre de l'erreur de login : statut/url/message uniquement, jamais le body
   * de la requête (credentials).
   */
  private describeError(error: unknown): Record<string, unknown> {
    if (error instanceof HttpErrorResponse) {
      return {
        kind: 'http',
        status: error.status,
        statusText: error.statusText,
        url: error.url || 'N/A',
        message: error.message,
      };
    }

    if (error instanceof Error) {
      return { kind: error.name, message: error.message };
    }

    return { kind: 'unknown', message: error == null ? 'no_error_details' : String(error) };
  }
}
